/**
 * TrameProgression.jsx — Composant partagé qui rend une progression pédagogique
 * multi-phases (Plateau A1/A2, Formation 125, Passerelle A2→A).
 *
 * Rend la grille pédagogique enrichie 12 champs :
 *  🎯 Objectif · Pourquoi · Comment · 🏍️ Exercices · 👀 Observation ·
 *  ⚠️ Risques · 🌦️ Influences · Erreurs · 🔧 Remédiation ·
 *  🗣️ Questions · ✅ Critère de réussite · ✨ À retenir
 *
 * Mode enseignant (projection large) / mode élève (accordéon mobile-friendly).
 * Compatible impression / PDF, avec bouton copie synthèse markdown.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Copy, GraduationCap, User, Presentation, Clipboard,
  Target, AlertTriangle, Wind, HelpCircle, Sparkles, ChevronRight,
  BookOpenCheck, Home, ChevronDown, Check, Clock, Map as MapIcon,
  Building2, Bike, Eye, X as XIcon, Wrench, MessageCircle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { toTu, toTuArr } from '../../utils/toTu';
import { useApp } from '../../contexts/AppContext';
import useAudience, { AUDIENCES } from '../../hooks/useAudience';
import useLivretSync from '../../hooks/useLivretSync';
import TrameQR, { TrameQRToggle } from './TrameQR';
import TrameSessionQR from './TrameSessionQR';
import TrameTeacherQR from './TrameTeacherQR';
import TrameLivretQR from './TrameLivretQR';
import TrameDebriefPanel from './TrameDebriefPanel';
import TrameAutoEval from './TrameAutoEval';
import TrameStudentNextSteps from './TrameStudentNextSteps';
import PlateauSessionSummary from '../plateau/PlateauSessionSummary';

const CONTEXT_META = {
  salle:        { icon: Building2, label: 'En salle',         tone: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30' },
  plateau:      { icon: MapIcon,   label: 'Hors circulation', tone: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30' },
  circulation:  { icon: Bike,      label: 'En circulation',   tone: 'text-orange-300 bg-orange-500/15 border-orange-500/30' },
};

const NIVEAU_COLOR = ['#64748b', '#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444', '#a855f7'];

function BulletList({ icon: Icon, title, items, tone = 'white' }) {
  if (!items || !items.length) return null;
  const toneMap = {
    white: 'text-white', rose: 'text-rose-300', amber: 'text-amber-300',
    emerald: 'text-emerald-300', sky: 'text-sky-300',
  };
  return (
    <div className="space-y-1.5">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white/70">
        <Icon className={`w-3.5 h-3.5 ${toneMap[tone]}`} /> {title}
      </div>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-white/80 text-[13px] leading-relaxed flex gap-1.5">
            <span className={`shrink-0 mt-1.5 w-1 h-1 rounded-full bg-current ${toneMap[tone]}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TextBlock({ icon: Icon, title, text, toneClass }) {
  if (!text) return null;
  return (
    <div className="space-y-2">
      <div className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${toneClass}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <p className="text-white/85 leading-relaxed">{text}</p>
    </div>
  );
}

function ExercicesList({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="space-y-2">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white/70">
        <Bike className="w-3.5 h-3.5 text-white" /> Exercices proposés
      </div>
      <ul className="space-y-2">
        {items.map((ex, i) => {
          const col = NIVEAU_COLOR[ex.niveau || 0];
          return (
            <li key={i} className="rounded-lg bg-black/30 border border-white/5 p-3">
              <div className="flex items-start gap-2 flex-wrap">
                {typeof ex.niveau === 'number' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest border" style={{ color: col, borderColor: `${col}66`, background: `${col}12` }}>
                    N{ex.niveau}
                  </span>
                )}
                <span className="text-white font-black text-[13px]">{ex.nom}</span>
              </div>
              <p className="text-white/70 text-[12.5px] leading-relaxed mt-1.5">{ex.description}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ExerciseCardTeacher({ ex, phaseId }) {
  return (
    <article className="rounded-2xl bg-[#141416] border border-white/10 hover:border-white/20 transition overflow-hidden print:break-inside-avoid print:border-black/20" id={`ex-${ex.id}`} data-testid={`ex-${ex.id}`}>
      <div className="p-4">
        <h3 className="font-black text-white leading-tight text-xl sm:text-2xl mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {ex.title}
        </h3>
        <div className="space-y-4 text-[15px]">
          {ex.objectif && (
            <div className="rounded-xl bg-emerald-950/25 border border-emerald-500/25 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-emerald-200 mb-1">
                <Target className="w-3 h-3" /> Objectif
              </div>
              <p className="text-emerald-50 font-semibold text-[13.5px] leading-relaxed">{ex.objectif}</p>
            </div>
          )}
          <TextBlock icon={HelpCircle} title="Pourquoi ?" text={ex.pourquoi} toneClass="text-sky-300" />
          <BulletList icon={GraduationCap} title="Comment ?" items={ex.comment} tone="sky" />
          <ExercicesList items={ex.exercices} />
          <div className="grid sm:grid-cols-2 gap-4">
            <BulletList icon={Eye} title="👀 Observation" items={ex.observation} tone="amber" />
            <BulletList icon={AlertTriangle} title="⚠️ Risques" items={ex.risques} tone="rose" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <BulletList icon={Wind} title="🌦️ Influences" items={ex.influences} tone="amber" />
            <BulletList icon={XIcon} title="Erreurs fréquentes" items={ex.erreurs} tone="rose" />
          </div>
          <BulletList icon={Wrench} title="🔧 Remédiation" items={ex.remediation} tone="sky" />
          {(ex.question || (ex.questions?.length)) && (
            <div className="rounded-xl bg-rose-950/30 border border-rose-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-rose-200 mb-1"><MessageCircle className="w-3 h-3" /> Questions pédagogiques</div>
              {ex.question && <p className="text-rose-100 italic text-[13.5px]">{ex.question}</p>}
              {ex.questions?.length && (
                <ul className="space-y-1 mt-1">
                  {ex.questions.map((q, i) => <li key={i} className="text-rose-100 italic text-[13.5px]">{q}</li>)}
                </ul>
              )}
            </div>
          )}
          {ex.critere && (
            <div className="rounded-xl bg-emerald-950/30 border border-emerald-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-emerald-200 mb-1"><CheckCircle2 className="w-3 h-3" /> Critère de réussite</div>
              <p className="text-emerald-100 font-bold text-[13.5px]">{ex.critere}</p>
            </div>
          )}
          {ex.aRetenir && (
            <div className="rounded-xl bg-amber-950/25 border border-amber-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-amber-200 mb-1"><Sparkles className="w-3 h-3" /> À retenir</div>
              <p className="text-amber-100 font-bold text-[13.5px]">{ex.aRetenir}</p>
            </div>
          )}
          {ex.debrief && (
            <div className="rounded-xl bg-[#0f0f10] border border-white/10 px-3 py-3">
              <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-white/70 mb-2"><Clipboard className="w-3 h-3" /> Débrief élève</div>
              <ul className="space-y-1">
                {ex.debrief.map((d, i) => <li key={i} className="text-white/80 text-[13px]">{d}</li>)}
              </ul>
            </div>
          )}

          {/* QR code — projeter/imprimer pour que l'élève puisse rescan après le cours */}
          <TrameQR exId={ex.id} phaseId={phaseId} mode="student" label="Scan élève — après cours" />
        </div>
      </div>
    </article>
  );
}

/**
 * Mode ÉLÈVE — version courte et TU. Ne montre que ce qui parle à l'élève :
 * objectif (converti en TU), Comment ? (bullets), critère de réussite (TU),
 * à retenir. Le reste (observation, erreurs, remédiation, questions
 * pédagogiques) est masqué car réservé à l'enseignant.
 */
function ExerciseCardStudent({ ex, phaseId, initiallyOpen = false }) {
  const [open, setOpen] = useState(initiallyOpen);
  useEffect(() => { if (initiallyOpen) setOpen(true); }, [initiallyOpen]);
  const objectif = toTu(ex.objectif || '');
  const critere = toTu(ex.critere || '');
  const comment = toTuArr(ex.comment || []);
  // Débrief : questions pédagogiques converties en 2ᵉ personne pour l'élève.
  //   « Où sont tes points d'appui sur la moto ? » etc.
  const debriefQuestions = useMemo(() => {
    const raw = ex.questions && ex.questions.length ? ex.questions : (ex.question ? [ex.question] : []);
    return toTuArr(raw);
  }, [ex.questions, ex.question]);
  return (
    <article className="rounded-2xl bg-[#141416] border border-white/10 hover:border-white/20 transition overflow-hidden print:break-inside-avoid print:border-black/20" id={`ex-${ex.id}`} data-testid={`ex-${ex.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <h3 className="font-black text-white leading-tight text-base sm:text-lg" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {ex.title}
        </h3>
        <ChevronDown className={`w-4 h-4 text-white/50 transition print:hidden ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 text-[13px]">
          {objectif && (
            <div className="rounded-xl bg-emerald-950/25 border border-emerald-500/25 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-200 mb-1">
                <Target className="w-3 h-3" /> Ton objectif
              </div>
              <p className="text-emerald-50 font-semibold text-[13.5px] leading-relaxed">{objectif}</p>
            </div>
          )}
          {comment.length > 0 && (
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-300">
                <GraduationCap className="w-3 h-3" /> Ce que tu fais
              </div>
              <ul className="space-y-0.5">
                {comment.map((c, i) => (
                  <li key={i} className="text-white/85 text-[13px] leading-relaxed flex gap-1.5">
                    <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-sky-300" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {critere && (
            <div className="rounded-xl bg-emerald-950/30 border border-emerald-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-200 mb-1"><CheckCircle2 className="w-3 h-3" /> Tu as réussi quand</div>
              <p className="text-emerald-100 font-bold text-[13.5px]">{critere}</p>
            </div>
          )}
          {ex.aRetenir && (
            <div className="rounded-xl bg-amber-950/25 border border-amber-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-200 mb-1"><Sparkles className="w-3 h-3" /> À retenir</div>
              <p className="text-amber-100 font-bold text-[13.5px]">{ex.aRetenir}</p>
            </div>
          )}

          {debriefQuestions.length > 0 && (
            <div className="rounded-xl bg-rose-950/25 border border-rose-500/30 px-3 py-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-rose-200 mb-1">
                <MessageCircle className="w-3 h-3" /> Débrief · pose-toi la question
              </div>
              <ul className="space-y-1">
                {debriefQuestions.map((q, i) => (
                  <li key={i} className="text-rose-100 italic text-[13px] leading-relaxed">{q}</li>
                ))}
              </ul>
            </div>
          )}

          {/* QR compact — toggle « QR après cours » pour ne pas alourdir la carte */}
          <div className="pt-1">
            <TrameQRToggle exId={ex.id} phaseId={phaseId} mode="student" />
          </div>
        </div>
      )}
    </article>
  );
}

function ExerciseCard({ ex, mode, phaseId, initiallyOpen = false }) {
  return mode === 'teacher'
    ? <ExerciseCardTeacher ex={ex} phaseId={phaseId} />
    : <ExerciseCardStudent ex={ex} phaseId={phaseId} initiallyOpen={initiallyOpen} />;
}

/**
 * Génère une synthèse COURTE en TU pour UNE seule phase donnée.
 * Utilisé quand l'élève copie sa synthèse en fin de session : il ne veut
 * QUE ce qui a été travaillé aujourd'hui (pas les 3 phases).
 *
 * @param {string} title    Nom de la trame
 * @param {object} phase    Phase courante (code, title, duration, exercises…)
 * @param {object} autoEval Dict { [exId]: 'ok' | 'todo' | 'no' } — optionnel
 */
const AUTO_EVAL_LABELS = {
  ok:   { emoji: '✅', label: 'Je maîtrise' },
  todo: { emoji: '🔁', label: 'À retravailler' },
  no:   { emoji: '⏳', label: 'Pas encore' },
};

function buildStudentSyntheseFromPhase(title, phase, autoEval = {}) {
  const lines = [];
  const phaseTitle = phase.title || '';
  lines.push(`# ${title} — ${phase.code || ''} ${phaseTitle}${phase.duration ? ' (' + phase.duration + ')' : ''}`);
  lines.push('');
  if (phase.accroche) {
    lines.push(`_${toTu(phase.accroche)}_`);
    lines.push('');
  }
  phase.exercises.forEach(ex => {
    lines.push(`## ${ex.title}`);
    if (ex.objectif)  lines.push(`🎯 ${toTu(ex.objectif)}`);
    if (ex.aRetenir)  lines.push(`✨ ${ex.aRetenir}`);
    if (ex.critere)   lines.push(`✅ ${toTu(ex.critere)}`);
    lines.push('');
  });

  // Récap auto-évaluation si l'élève a marqué au moins une compétence
  const evaluated = phase.exercises.filter(ex => autoEval[ex.id]);
  if (evaluated.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push('## 📊 Mon auto-évaluation de la session');
    lines.push('');
    const total = phase.exercises.length;
    const ok = phase.exercises.filter(ex => autoEval[ex.id] === 'ok').length;
    const todo = phase.exercises.filter(ex => autoEval[ex.id] === 'todo').length;
    const no = phase.exercises.filter(ex => autoEval[ex.id] === 'no').length;
    const pct = total > 0 ? Math.round((ok / total) * 100) : 0;
    lines.push(`**Compétences maîtrisées : ${ok} / ${total} (${pct} %)**`);
    lines.push(`- ✅ Je maîtrise : ${ok}`);
    lines.push(`- 🔁 À retravailler : ${todo}`);
    lines.push(`- ⏳ Pas encore : ${no}`);
    lines.push('');
    lines.push('### Détail par compétence');
    phase.exercises.forEach(ex => {
      const st = autoEval[ex.id];
      const meta = st ? AUTO_EVAL_LABELS[st] : null;
      const stLabel = meta ? `${meta.emoji} ${meta.label}` : '— non évaluée';
      lines.push(`- **${ex.title}** — ${stLabel}`);
    });
    if (todo > 0 || no > 0) {
      lines.push('');
      lines.push('### 🎯 À travailler la prochaine séance');
      phase.exercises.forEach(ex => {
        if (autoEval[ex.id] === 'todo' || autoEval[ex.id] === 'no') {
          lines.push(`- ${ex.title}`);
        }
      });
    }
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Synthèse COURTE en TU sur TOUTES les phases (fallback / usage enseignant).
 * Utilisée comme fallback si la data ne fournit pas de buildSyntheseCourt().
 */
function buildStudentSyntheseFromPhases(title, intro, phases) {
  const lines = [];
  lines.push(`# ${title} — Version élève`);
  lines.push('');
  if (intro?.baseline) lines.push(`Méthode : ${intro.baseline}`);
  lines.push('');
  phases.forEach(phase => {
    lines.push(`## ${phase.code} · ${phase.title}${phase.duration ? ' (' + phase.duration + ')' : ''}`);
    lines.push('');
    phase.exercises.forEach(ex => {
      lines.push(`### ${ex.title}`);
      if (ex.objectif)  lines.push(`🎯 ${toTu(ex.objectif)}`);
      if (ex.aRetenir)  lines.push(`✨ ${ex.aRetenir}`);
      if (ex.critere)   lines.push(`✅ ${toTu(ex.critere)}`);
      lines.push('');
    });
  });
  return lines.join('\n');
}

export default function TrameProgression({
  title, subtitle, badge = 'Trame pédagogique CODE 2 ROUES',
  breadcrumbLabel, intro, phases, buildSynthese, buildSyntheseCourt,
  testid = 'trame-progression',
  trameKey, // ex: '125' | 'plateau' | 'passerelle' — sert de préfixe autoeval
  focusBlock = null, // { title, intro, steps, aRetenir, disclaimer, visibleOn: ['p2','p3'] }
}) {
  const { user } = useApp();
  const { audience } = useAudience();

  // Le mode « Enseignant » n'est proposé qu'aux utilisateurs identifiés
  // comme enseignant/admin ou aux visiteurs ayant explicitement choisi
  // l'audience « enseignant » / « futur enseignant ». Les visiteurs sans
  // compte (élèves par défaut) voient toujours le mode élève, sans toggle.
  const isTeacherRole = !!(user && (user.role === 'enseignant' || user.role === 'admin' || user.is_admin));
  const isTeacherAudience = audience === AUDIENCES.ENSEIGNANT || audience === AUDIENCES.FUTUR_ENSEIGNANT;
  const canSeeTeacherMode = isTeacherRole || isTeacherAudience;

  // Slug de la trame pour la clé auto-évaluation (fallback sur testid)
  const safeTrameKey = trameKey || (testid || 'trame').replace(/^trame-?/, '') || 'trame';

  // Deep-link : ?ex=<id>&mode=<student|teacher>&phase=<phaseId>&view=<debrief>
  // Permet à un QR scanné en fin de cours d'ouvrir la trame sur la bonne
  // compétence, en mode élève par défaut. Vue « debrief » disponible pour
  // les enseignants (cockpit compact critères + questions + débrief).
  const initialQuery = useMemo(() => {
    if (typeof window === 'undefined') return { ex: null, mode: null, phaseId: null, view: null };
    const p = new URLSearchParams(window.location.search);
    return { ex: p.get('ex'), mode: p.get('mode'), phaseId: p.get('phase'), view: p.get('view') };
  }, []);

  const initialPhaseIdx = useMemo(() => {
    if (initialQuery.phaseId) {
      const idx = phases.findIndex(p => p.id === initialQuery.phaseId);
      if (idx >= 0) return idx;
    }
    if (initialQuery.ex) {
      const idx = phases.findIndex(p => (p.exercises || []).some(e => e.id === initialQuery.ex));
      if (idx >= 0) return idx;
    }
    return 0;
  }, [phases, initialQuery]);

  const initialMode = (initialQuery.mode === 'teacher' && canSeeTeacherMode) ? 'teacher' : 'student';

  const [mode, setMode] = useState(initialMode);
  const [phaseIdx, setPhaseIdx] = useState(initialPhaseIdx);
  const [copied, setCopied] = useState(false);
  // Vue « debrief » activable via QR moniteur ou via bouton dans la toolbar
  const [debriefView, setDebriefView] = useState(
    initialQuery.view === 'debrief' && (initialMode === 'teacher' || canSeeTeacherMode)
  );

  // Verrou : si le visiteur perd son statut « enseignant » (déconnexion,
  // changement d'audience), on force le mode élève.
  useEffect(() => {
    if (!canSeeTeacherMode && mode !== 'student') setMode('student');
  }, [canSeeTeacherMode, mode]);

  // Auto-scroll vers l'exercice ciblé quand la page se charge via QR
  useEffect(() => {
    if (!initialQuery.ex || typeof window === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`ex-${initialQuery.ex}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-rose-500', 'ring-offset-2', 'ring-offset-black');
        setTimeout(() => el.classList.remove('ring-2', 'ring-rose-500', 'ring-offset-2', 'ring-offset-black'), 3000);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [initialQuery.ex, phaseIdx]);

  const phase = phases[phaseIdx];

  // Auto-évaluation persistée pour la phase courante — même clé que
  // TrameAutoEval, permet d'enrichir la synthèse copiée avec le recap.
  const autoEvalStorageKey = `code2roues:autoeval-${safeTrameKey}-${phase.id}:v1`;
  const {
    state: autoEvalState,
    setValue: setAutoEvalValue,
    syncing: autoEvalSyncing,
  } = useLivretSync(autoEvalStorageKey);

  // Synthèse copiée :
  //  - mode élève  → UNIQUEMENT la phase en cours + récap auto-évaluation
  //  - mode enseignant → synthèse complète des 3 phases (pour archivage)
  const syntheseLongue = useMemo(() => buildSynthese(), [buildSynthese]);
  const syntheseCourte = useMemo(
    () => buildStudentSyntheseFromPhase(title, phase, autoEvalState),
    [title, phase, autoEvalState],
  );
  const synthese = mode === 'student' ? syntheseCourte : syntheseLongue;
  const ctxMeta = phase?.context ? CONTEXT_META[phase.context] : null;

  const showFocus = focusBlock && (!focusBlock.visibleOn || focusBlock.visibleOn.includes(phase.id));

  const copySynthese = async () => {
    try {
      await navigator.clipboard.writeText(synthese);
      setCopied(true);
      const evaluatedCount = mode === 'student'
        ? phase.exercises.filter(ex => autoEvalState[ex.id]).length
        : 0;
      toast.success(
        mode === 'student'
          ? evaluatedCount > 0
            ? `Synthèse ${phase.code || ''} copiée avec ton auto-évaluation (${evaluatedCount} compétence${evaluatedCount > 1 ? 's' : ''} notée${evaluatedCount > 1 ? 's' : ''}).`
            : `Synthèse ${phase.code || ''} copiée. Astuce : note ton auto-évaluation avant de copier pour l'inclure.`
          : 'Synthèse complète copiée (version enseignant).'
      );
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error('Copie impossible. Ouvre la synthèse puis copie manuellement.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 lg:pb-16" data-testid={testid}>
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/formations-2-roues" className="hover:text-white">Trames pédagogiques</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">{breadcrumbLabel}</span>
        </div>
      </div>

      {/* Hero — VERSION COMPACTE en mode élève */}
      <header className={`max-w-6xl mx-auto px-4 sm:px-6 ${mode === 'student' ? 'py-4 sm:py-5' : 'py-8 sm:py-10'}`}>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest mb-2 sm:mb-3">
          <BookOpenCheck className="w-3 h-3" /> {badge}
        </div>
        <h1
          className={`font-black text-white leading-tight ${mode === 'student' ? 'text-xl sm:text-2xl md:text-3xl' : 'text-3xl sm:text-4xl md:text-5xl'}`}
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {title}
        </h1>
        {mode !== 'student' && (
          <p className="text-white/70 text-sm sm:text-base mt-2 max-w-3xl leading-relaxed">{subtitle}</p>
        )}

        {/* Grille "Méthode 12 champs" — masquée en mode élève (technique enseignant) */}
        {mode !== 'student' && (
          <div className="mt-4 rounded-xl bg-[#141416] border border-white/10 p-4 text-white/80 text-sm">
            <strong className="text-white">Méthode :</strong> <span className="text-rose-300 font-mono text-[11.5px] sm:text-[13px]">{intro.baseline}</span>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
              {intro.questions.map(q => (
                <div key={q.key} className="rounded-lg bg-black/40 border border-white/10 p-2 text-[11px]">
                  <div className="text-white font-black uppercase tracking-widest text-[10px] mb-0.5">{q.label}</div>
                  <div className="text-white/60">{q.text}</div>
                </div>
              ))}
            </div>
            {intro.principe && (
              <p className="mt-3 text-[12px] text-amber-200/90 border-t border-white/5 pt-3 italic">{intro.principe}</p>
            )}
            {intro.regleDuree && (
              <p className="mt-3 text-[12px] text-amber-200/90 border-t border-white/5 pt-3 italic">{intro.regleDuree}</p>
            )}
          </div>
        )}

        {/* Toolbar : mode + copie */}
        <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 print:hidden">
          {canSeeTeacherMode && (
            <div className="inline-flex rounded-xl border border-white/10 bg-[#141416] p-0.5" data-testid="mode-toggle-wrapper">
              <button onClick={() => setMode('teacher')} className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[11px] font-black uppercase tracking-widest transition ${mode === 'teacher' ? 'bg-rose-600 text-white' : 'text-white/60 hover:text-white'}`} data-testid="mode-teacher">
                <Presentation className="w-3.5 h-3.5" /> Enseignant
              </button>
              <button onClick={() => setMode('student')} className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[11px] font-black uppercase tracking-widest transition ${mode === 'student' ? 'bg-rose-600 text-white' : 'text-white/60 hover:text-white'}`} data-testid="mode-student">
                <User className="w-3.5 h-3.5" /> Élève
              </button>
            </div>
          )}
          {canSeeTeacherMode && mode === 'teacher' && (
            <button
              type="button"
              onClick={() => setDebriefView(v => !v)}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition border ${debriefView ? 'bg-amber-500/15 border-amber-500/40 text-amber-200' : 'bg-white/5 border-white/15 text-white/70 hover:text-white'}`}
              data-testid="toggle-debrief-view"
              aria-pressed={debriefView}
            >
              <Presentation className="w-3.5 h-3.5" /> {debriefView ? 'Masquer débrief' : 'Vue débrief'}
            </button>
          )}
          <button type="button" onClick={copySynthese} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl border border-emerald-500/40 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 text-[11px] font-black uppercase tracking-widest transition" data-testid="copy-synthese">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {(() => {
              if (copied) return 'Copié !';
              if (mode === 'teacher') return 'Copier la synthèse complète';
              const nb = phase.exercises.filter(ex => autoEvalState[ex.id]).length;
              const suffix = nb > 0 ? ` + ${nb}★ auto-éval` : '';
              return `Copier ma session (${phase.code || phase.id})${suffix}`;
            })()}
          </button>
          {/* Imprimer masqué en mode élève (superflu sur téléphone) */}
          {mode !== 'student' && (
            <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 text-[11px] font-black uppercase tracking-widest transition" data-testid="print-synthese">
              Imprimer / PDF
            </button>
          )}
        </div>
      </header>

      {/* Phase selector */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 print:hidden">
        <div className={`grid gap-2 ${phases.length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {phases.map((p, i) => {
            const meta = p.context ? CONTEXT_META[p.context] : null;
            const Icon = meta?.icon;
            return (
              <button
                key={p.id}
                onClick={() => setPhaseIdx(i)}
                className={`rounded-xl p-3 sm:p-4 text-left border transition ${phaseIdx === i ? 'bg-[#181819] border-white/30 shadow-lg' : 'bg-[#141416] border-white/10 hover:border-white/20'}`}
                style={phaseIdx === i ? { borderColor: p.color } : {}}
                data-testid={`phase-tab-${p.id}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[10px] font-mono font-black tracking-widest" style={{ color: p.color }}>{p.code}</div>
                  {Icon && <Icon className="w-3.5 h-3.5" style={{ color: p.color }} />}
                </div>
                <div className="text-white text-[13px] sm:text-[15px] font-black mt-0.5 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{p.title}</div>
                <div className="text-white/50 text-[10.5px] mt-1 line-clamp-2">{p.subtitle}</div>
                {p.duration && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-white/60">
                    <Clock className="w-2.5 h-2.5" /> {p.duration}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="rounded-2xl p-4 mb-4 border" style={{ background: `${phase.color}12`, borderColor: `${phase.color}44` }}>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-mono font-black tracking-widest text-[11px]" style={{ color: phase.color }}>{phase.code}</span>
            <h2 className="text-white text-xl sm:text-2xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{phase.title}</h2>
            {ctxMeta && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${ctxMeta.tone}`}>
                <ctxMeta.icon className="w-3 h-3" /> {ctxMeta.label}
              </span>
            )}
            {phase.duration && (
              <span className="inline-flex items-center gap-1 text-[11px] text-white/60"><Clock className="w-3 h-3" /> {phase.duration}</span>
            )}
          </div>
          <p className="text-white/80 text-[13.5px] mt-2 leading-relaxed">{phase.accroche}</p>
          {phase.disclaimer && (
            <p className="mt-2 text-[12px] text-amber-200/80 italic border-t border-white/5 pt-2">{phase.disclaimer}</p>
          )}
        </div>

        {/* Bloc focus manœuvre — remonté juste après l'intro de phase pour
            que l'élève lise le focus AVANT les cartes compétences. */}
        {showFocus && (
          <section className="mb-4 rounded-2xl bg-gradient-to-br from-rose-950/40 via-[#141416] to-[#141416] border border-rose-500/30 p-4 sm:p-5" data-testid="focus-block-top">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-rose-200 mb-2">
              <BookOpenCheck className="w-3 h-3" /> Focus manœuvre
            </div>
            <h3 className="text-white text-lg sm:text-xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {focusBlock.title}
            </h3>
            <p className="text-white/75 text-[13px] mt-1.5">{focusBlock.intro}</p>
            <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
              {focusBlock.steps.map(s => (
                <div key={s.n} className="rounded-xl bg-black/40 border border-white/10 p-2.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-5 h-5 rounded-full bg-rose-600 text-white text-[10px] font-black flex items-center justify-center">{s.n}</span>
                    <h4 className="text-white font-black text-[12.5px]">{s.title}</h4>
                  </div>
                  <ul className="space-y-0.5">
                    {s.bullets.map((b, i) => <li key={i} className="text-white/75 text-[12px] leading-snug">• {b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            {focusBlock.aRetenir && (
              <div className="mt-3 rounded-xl bg-emerald-950/30 border border-emerald-500/30 px-3 py-2">
                <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-emerald-200 mb-1"><Sparkles className="w-3 h-3" /> À retenir</div>
                <p className="text-emerald-100 font-bold text-[13px]">{focusBlock.aRetenir}</p>
              </div>
            )}
            {focusBlock.disclaimer && (
              <p className="mt-2.5 text-[11px] text-amber-200/80 italic">{focusBlock.disclaimer}</p>
            )}
          </section>
        )}

        {/* Panneau debrief moniteur — visible uniquement en mode enseignant
            + vue debrief active (arrivée via QR moniteur ou toggle). */}
        {mode === 'teacher' && debriefView && (
          <TrameDebriefPanel phase={phase} />
        )}

        <div className={mode === 'teacher' ? 'space-y-3' : 'grid sm:grid-cols-2 gap-3'}>
          {phase.exercises.map(ex => (
            <ExerciseCard
              key={ex.id}
              ex={ex}
              mode={mode}
              phaseId={phase.id}
              initiallyOpen={initialQuery.ex === ex.id}
            />
          ))}
        </div>

        {/* QR de fin de session — regroupe TOUTES les compétences travaillées
            aujourd'hui. Projeté en fin de séance par l'enseignant, scanné par
            les élèves pour retrouver la session sur leur téléphone. */}
        <TrameSessionQR
          phaseId={phase.id}
          phaseCode={phase.code}
          phaseTitle={phase.title}
          color={phase.color || '#e11d48'}
        />

        {/* Auto-évaluation élève — affichée en mode élève, juste après le QR
            de session. État hoisté au parent (partagé avec la synthèse copiée). */}
        {mode === 'student' && (
          <TrameAutoEval
            phase={phase}
            color={phase.color || '#e11d48'}
            state={autoEvalState}
            setValue={setAutoEvalValue}
            syncing={autoEvalSyncing}
          />
        )}

        {/* Prochaines étapes — livret + création de compte (guests).
            Simplifie la sortie du flow session en mode élève. */}
        {mode === 'student' && (
          <TrameStudentNextSteps
            trameKey={safeTrameKey}
            color={phase.color || '#e11d48'}
            evaluatedCount={phase.exercises.filter(ex => autoEvalState[ex.id]).length}
            masteredCount={phase.exercises.filter(ex => autoEvalState[ex.id] === 'ok').length}
          />
        )}

        {/* QR Premium Moniteur — visible seulement pour les enseignants.
            Ouvre le cockpit débrief compact (critères + questions + débrief). */}
        {canSeeTeacherMode && (
          <div className="mt-4">
            <TrameTeacherQR
              phaseId={phase.id}
              phaseCode={phase.code}
              phaseTitle={phase.title}
            />
          </div>
        )}

        {/* QR Livret — chapitre ciblé du livret interactif.
            En mode enseignant on affiche systématiquement pour partage rapide.
            En mode élève on l'affiche aussi pour permettre à l'élève de
            ré-ouvrir directement le bon chapitre sur son téléphone. */}
        <TrameLivretQR
          trameKey={safeTrameKey}
          phaseId={phase.id}
          phaseCode={phase.code}
        />

        {/* Résumé auto-école — visible pour les enseignants uniquement, sur la
            trame Plateau. Génère le bloc Markdown à coller dans Ediser /
            Packweb / ClickNLearn en fin de séance. */}
        {canSeeTeacherMode && safeTrameKey === 'plateau' && (
          <PlateauSessionSummary
            courseSlug={`trame-plateau-${phase.id}`}
            courseTitle={`Trame Plateau · ${phase.code} · ${phase.title}`}
            courseCode={`Trame ${phase.code}`}
            subtitle={phase.subtitle}
            mantra={phase.accroche ? phase.accroche.slice(0, 120) + (phase.accroche.length > 120 ? '…' : '') : ''}
            duree={phase.duration || '2 h'}
            defaultPhaseIds={[phase.id]}
          />
        )}

        {/* Bloc focus manœuvre — désormais remonté juste après l'intro de phase.
            Ancienne position vidée pour éviter le doublon. */}

        {/* Navigation phases */}
        <div className="mt-8 flex items-center justify-between gap-3 print:hidden">
          <button type="button" onClick={() => setPhaseIdx(i => Math.max(0, i - 1))} disabled={phaseIdx === 0} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10 text-[12px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:pointer-events-none">
            <ArrowLeft className="w-3.5 h-3.5" /> Phase précédente
          </button>
          <button type="button" onClick={() => setPhaseIdx(i => Math.min(phases.length - 1, i + 1))} disabled={phaseIdx === phases.length - 1} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[12px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:pointer-events-none">
            Phase suivante <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </main>
    </div>
  );
}
